import { useState, useEffect } from 'react'
import { useCurrentUser } from '@/hooks/use-current-user'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/UI/DropdownMenu'
import useTokenStore from '@/store/store'
import useThemeStore, { FontOption, ThemeMode } from '@/store/theme'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Separator } from '../UI/Separator'
import RadioGroupTab from '../UI/Radio/RadioGroupTab'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../UI/Select'
import { Button } from '../UI/Button'
import defaultAvatar from '@/assets/images/profile-avatar-default.png'
import GlobalSearch from '@/components/GlobalSearch'


const Headbar = () => {
  const { user, isLoading } = useCurrentUser()
  const { theme, font, switchThemeMode, switchFont } = useThemeStore()
  const [searchOpen, setSearchOpen] = useState(false)
  const clearToken = useTokenStore((state) => state.clearToken)
  const navigate = useNavigate()

  // 键盘快捷键支持 Ctrl/Cmd + K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleCopyPhoneNumber = async () => {
    const phone = user?.phone
    if (!phone) {
      toast.error('暂无手机号可复制')
      return
    }
    try {
      await navigator.clipboard.writeText(phone)
      toast.success('手机号已复制')
    } catch (error) {
      console.error('Failed to copy phone number', error)
      toast.error('复制失败，请稍后重试')
    }
  }

  const handleThemeChange = (value: string) => switchThemeMode(value as ThemeMode)
  const handleFontChange = (value: string) => switchFont(value as FontOption)

  const handleLogout = () => {
    clearToken()
    localStorage.removeItem('userInfo')
    toast.success('已退出登录')
    navigate('/login')
  }

  return (
    <header className='flex h-64px items-center justify-between bor-2 bor-b px-6'>
      {/* 搜索触发按钮 */}
      <Button
        variant='secondary'
        onClick={() => setSearchOpen(true)}
        className='justify-between gap-1.5 w-[320px] pr-1.5'
        aria-label='搜索作品'
      >
        <div className='flex items-center gap-1.5 [&>iconpark-icon]:color-text-tertiary'>
          <iconpark-icon name='outline-search' size='16' aria-hidden='true' />
          <span className='text-secondary'>搜索作品</span>
        </div>
        <div className='flex items-center gap-0.5'>
          <kbd className='flex items-center justify-center h-5 px-0.5 min-w-5 text-body-sm text-tertiary bg-surface rounded-1'>
            {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}
          </kbd>
          <kbd className='flex items-center justify-center h-5 px-0.5 min-w-5 text-body-sm text-tertiary bg-surface rounded-1'>
            K
          </kbd>
        </div>
      </Button>

      {/* 全局搜索对话框 */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      
      <div className='flex items-center gap-3'>
        <DropdownMenu>
          <DropdownMenuTrigger className='outline-none' asChild>
            <button
              type='button'
              className='flex items-center rounded-l-6 rounded-r-2.5 hover:bg-overlay-1 cursor-pointer transition-200 data-[state=open]:bg-overlay-1'
            >
              <div className='flex aspect-square overflow-hidden size-8 bg-surground bor-2 rounded-full'>
                <img
                  src={defaultAvatar}
                  className='size-full object-cover rounded-full dark:brightness-[0.6]'
                />
              </div>
              <div className='flex px-2 text-body-md text-primary'>
                  {isLoading ? '加载中...' : user?.name || '未知用户'}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-200px'>
            <div className='flex flex-col gap-.5 text-body-md text-primary'>
              <div className='flex flex-col px-1 py-.5 hover:bg-overlay-1 rounded-2'>
                <label className='text-body-md-bold'>{user?.name || '未知用户'}</label>
                <div className='flex items-center gap-1'>
                  <span className='text-body-sm text-tertiary'>{user?.phone || '未知手机号'}</span>
                  <Button 
                    variant='tertiary' 
                    size='smIcon'
                    className='size-5 text-tertiary hover:text-primary'
                    onClick={handleCopyPhoneNumber}
                  >
                    <iconpark-icon name='outline-copy' size='14' />
                  </Button>
                </div>
              </div>
              <Separator className='my-1.5' />
              <div className='flex items-center justify-between gap-1 px-2 py-1 rounded-2 hover:bg-overlay-1'>
                <label className='text-body-md text-primary'>主题</label>
                <RadioGroupTab
                  value={theme}
                  onValueChange={handleThemeChange}
                  options={[
                    {
                      value: 'light',
                      label: (
                        <>
                          <iconpark-icon name='outline-sun' size='16' aria-hidden='true' />
                          <span className='sr-only'>亮色</span>
                        </>
                      )
                    },
                    {
                      value: 'dark',
                      label: (
                        <>
                          <iconpark-icon name='outline-moon' size='16' aria-hidden='true' />
                          <span className='sr-only'>暗色</span>
                        </>
                      )
                    }
                  ]}
                  size='sm'
                  className='flex'
                />
              </div>
              <div className='flex items-center justify-between gap-1 px-2 py-1 rounded-2 hover:bg-overlay-1'>
                <label className='text-body-md text-primary' htmlFor='font-select'>字体</label>
                <Select value={font} onValueChange={handleFontChange}>
                  <SelectTrigger id='font-select' size='sm' className='flex w-auto min-w-80px'>
                    <SelectValue placeholder='选择字体' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='song'>宋体</SelectItem>
                    <SelectItem value='system'>系统</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Separator className='my-1.5' />
              <DropdownMenuItem onClick={handleLogout}>
                <span>登出</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

export default Headbar
